import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  Modal,
  Selo,
  Tabela,
  useDialogo,
  type ColunaTabela,
  type TomSemantico,
} from '../../componentes/ui';
import { ErroApi } from '../../servicos/erros';
import { requisicaoAutenticada } from '../../servicos/http';
import { ErroDaApi } from '../ErroDaApi';

interface LinkResumo {
  readonly id: string;
  readonly linkType: string;
  readonly url: string;
  readonly expiresAt: string;
  readonly maxSubmissions: number;
  readonly currentSubmissions: number;
  readonly isActive: boolean;
  readonly expirado: boolean;
  readonly esgotado: boolean;
  readonly audCreatedAt: string;
}

type TipoDeConvite = 'colaboradores' | 'representantes';

/**
 * Os padrões do Deca 1, por tipo: o convite de colaboradores saía fixo com 72 h
 * e 100 envios (`GenerateLinkDialog.js`); o de representantes, 72 h e 5
 * (`GenerateRepresentantesLinkDialog.js`). Aqui continuam editáveis.
 */
const PADRAO_POR_TIPO: Readonly<Record<TipoDeConvite, { horas: string; maximo: string }>> = {
  colaboradores: { horas: '72', maximo: '100' },
  representantes: { horas: '72', maximo: '5' },
};

/** Os cinco papéis que a grade de representantes espera (V1). */
const PAPEIS_DE_REPRESENTANTES = [
  'Sócio administrador (1º representante legal)',
  'Sócio administrador (2º)',
  'Ponto focal (contato principal)',
  'Responsável contábil',
  'Responsável financeiro',
];

/**
 * RN-331 a RN-336 — Os convites de auto-cadastro, dentro de Colaboradores.
 *
 * ## Para que servem
 *
 * O escritório não digita os colaboradores do cliente: manda um link, e cada um
 * se cadastra. O link tem prazo e teto de envios — é uma capacidade, não um
 * endereço permanente. No Deca 1 o botão "Gerar Link" ficava no próprio drawer
 * de colaborador; a listagem dos links gerados não existia lá, e existe aqui
 * porque é como o gestor descobre que um convite venceu antes de repassá-lo.
 *
 * ## Revogar desativa, não apaga
 *
 * Um link revogado responde ao formulário público o mesmo que um inexistente,
 * mas a linha fica: as submissões que ele recebeu continuam apontando para ele.
 *
 * ## 403 é "não é para você", não erro
 *
 * Listar e gerar convites exige gerenciar a empresa. A aba que hospeda esta
 * seção decide pelo token quando pode (`AbaColaboradores`), mas há um caso que
 * só a API resolve — o gestor de outra empresa, que pode ou não ter cadastrado
 * este cliente. Para ele, um 403 significa que a seção não existe: some
 * inteira, sem caixa de erro e sem o botão "Gerar convite", que devolveria o
 * mesmo 403. Os outros erros (rede, 500) continuam aparecendo, porque aí há
 * algo a tentar de novo.
 */
export function SecaoDeConvites(props: {
  readonly businessId: string;
  /** Muda quando alguém de fora (o botão do topo, o modal de cadastro) pede o convite. */
  readonly pedidoDeGeracao?: number;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    if (props.pedidoDeGeracao !== undefined && props.pedidoDeGeracao > 0) setGerando(true);
  }, [props.pedidoDeGeracao]);
  const [copiado, setCopiado] = useState<string | null>(null);
  const { confirmar } = useDialogo();

  const links = useQuery({
    queryKey: ['links', props.businessId],
    queryFn: () => requisicaoAutenticada<LinkResumo[]>(`/business/${props.businessId}/links`),
    // Sem permissão não muda tentando de novo.
    retry: (tentativas, erro) => !semPermissao(erro) && tentativas < 2,
  });

  const revogar = useMutation({
    mutationFn: (linkId: string) =>
      requisicaoAutenticada<LinkResumo>(`/business/${props.businessId}/links/${linkId}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['links', props.businessId] });
    },
  });

  const colunas: readonly ColunaTabela<LinkResumo>[] = [
    {
      chave: 'linkType',
      titulo: 'Tipo',
      larguraMinima: '11rem',
      celula: (l) => (
        <span className="text-sm text-conteudo-primario">
          {l.linkType === 'representantes' ? 'Representantes' : 'Colaboradores'}
        </span>
      ),
    },
    {
      chave: 'situacao',
      titulo: 'Situação',
      larguraMinima: '8rem',
      celula: (l) => <Selo tom={tomDoLink(l)}>{situacao(l)}</Selo>,
    },
    {
      chave: 'envios',
      titulo: 'Usos',
      numerica: true,
      larguraMinima: '6rem',
      celula: (l) => (
        <span className="valor-fiscal text-sm">
          {l.currentSubmissions}/{l.maxSubmissions}
        </span>
      ),
    },
    {
      chave: 'expiresAt',
      titulo: 'Válido até',
      larguraMinima: '10rem',
      celula: (l) => (
        <span className="valor-fiscal whitespace-nowrap text-sm">
          {new Date(l.expiresAt).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '13rem',
      celula: (l) =>
        // Link vencido, esgotado ou revogado não se copia nem se revoga:
        // mandá-lo a alguém é mandar uma porta fechada.
        l.expirado || l.esgotado || !l.isActive ? (
          <span className="text-xs text-conteudo-sutil">Sem ações</span>
        ) : (
          <span className="inline-flex gap-2">
            <Botao
              variante="secundario"
              tamanho="pequeno"
              onClick={() => {
                void navigator.clipboard.writeText(l.url);
                setCopiado(l.id);
                window.setTimeout(() => setCopiado(null), 2000);
              }}
            >
              {copiado === l.id ? 'Copiado' : 'Copiar link'}
            </Botao>
            <Botao
              variante="sutil"
              tamanho="pequeno"
              carregando={revogar.isPending && revogar.variables === l.id}
              onClick={async () => {
                const ok = await confirmar({
                  titulo: 'Revogar este convite?',
                  descricao:
                    'Quem ainda não usou o link deixa de conseguir se cadastrar. Quem já se cadastrou continua.',
                  rotuloConfirmar: 'Revogar',
                  tom: 'perigo',
                });
                if (ok) revogar.mutate(l.id);
              }}
            >
              Revogar
            </Botao>
          </span>
        ),
    },
  ];

  const lista = links.data ?? [];

  if (links.isError && semPermissao(links.error)) return null;

  return (
    <section className="flex flex-col gap-4 border-t border-borda pt-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="font-display text-base font-semibold tracking-tight">Convites</h3>
          <p className="mt-1 max-w-3xl text-sm text-conteudo-secundario">
            Links para os próprios colaboradores ou representantes da empresa se cadastrarem, sem
            digitação aqui. Têm prazo e limite de usos — não são endereço permanente.
          </p>
        </div>
        <Botao variante="secundario" onClick={() => setGerando(true)}>
          Gerar convite
        </Botao>
      </div>

      {links.isError && <ErroDaApi titulo="Não foi possível carregar" erro={links.error} />}
      {revogar.isError && <ErroDaApi titulo="Não foi possível revogar" erro={revogar.error} />}

      {/* Sem convite, uma linha basta: o "Gerar convite" do cabeçalho já é o
          caminho, e um quadro vazio com outro botão igual só repetia. */}
      {!links.isPending && lista.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">Nenhum convite gerado ainda.</p>
      ) : (
        <Tabela
          legenda="Convites de auto-cadastro"
          colunas={colunas}
          dados={lista}
          chaveDaLinha={(l) => l.id}
          chaveDeCabecalhoDaLinha="linkType"
          carregando={links.isPending}
        />
      )}

      <ModalDeGeracao
        businessId={props.businessId}
        aberto={gerando}
        aoFechar={() => setGerando(false)}
        aoGerar={() => {
          void queryClient.invalidateQueries({ queryKey: ['links', props.businessId] });
        }}
      />
    </section>
  );
}

/* ─── Geração ─────────────────────────────────────────────────────────────── */

function ModalDeGeracao(props: {
  readonly businessId: string;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  readonly aoGerar: () => void;
}): JSX.Element {
  const [tipo, setTipo] = useState<TipoDeConvite>('colaboradores');
  const [horas, setHoras] = useState(PADRAO_POR_TIPO.colaboradores.horas);
  const [maximo, setMaximo] = useState(PADRAO_POR_TIPO.colaboradores.maximo);

  const gerar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<LinkResumo & { token: string }>(`/business/${props.businessId}/links`, {
        metodo: 'POST',
        corpo: {
          linkType: tipo,
          expirationHours: Number(horas),
          maxSubmissions: Number(maximo),
        },
      }),
    onSuccess: props.aoGerar,
  });

  const fechar = (): void => {
    gerar.reset();
    props.aoFechar();
  };

  const trocarTipo = (novo: TipoDeConvite): void => {
    // Trocar o tipo repõe os padrões daquele tipo: o gestor que só mexeu no
    // seletor recebe o convite que o Deca 1 gerava para ele.
    setTipo(novo);
    setHoras(PADRAO_POR_TIPO[novo].horas);
    setMaximo(PADRAO_POR_TIPO[novo].maximo);
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Gerar convite de cadastro"
      largura="larga"
      rodape={
        gerar.isSuccess ? (
          <Botao onClick={fechar}>Concluir</Botao>
        ) : (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao onClick={() => gerar.mutate()} carregando={gerar.isPending}>
              Gerar
            </Botao>
          </>
        )
      }
    >
      {gerar.isSuccess ? (
        <div className="flex flex-col gap-3">
          <Aviso tom="positivo" titulo="Convite gerado">
            Copie o endereço abaixo e envie a quem vai se cadastrar. Ele também foi enviado para o
            e-mail cadastrado da empresa. Quem usar o link entra na hora, sem aprovação manual.
          </Aviso>
          <p className="valor-fiscal select-all break-all rounded-card border border-borda bg-superficie-base px-3 py-2 text-xs">
            {gerar.data.url}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Botao
              variante="secundario"
              onClick={() => void navigator.clipboard.writeText(gerar.data.url)}
            >
              Copiar
            </Botao>
            <Selo tom="neutro" tamanho="pequeno">
              Expira em{' '}
              {new Date(gerar.data.expiresAt).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Selo>
            <Selo tom="neutro" tamanho="pequeno">
              {gerar.data.currentSubmissions}/{gerar.data.maxSubmissions} usos
            </Selo>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <CampoDeSelecao
            rotulo="Tipo de convite"
            value={tipo}
            onChange={(e) => trocarTipo(e.target.value as TipoDeConvite)}
            ajuda="Colaborador cadastra uma pessoa; representantes recebe a grade de quem assina pela empresa."
            opcoes={[
              { valor: 'colaboradores', rotulo: 'Colaborador' },
              { valor: 'representantes', rotulo: 'Representantes legais' },
            ]}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <CampoDeSelecao
              rotulo="Validade"
              value={horas}
              onChange={(e) => setHoras(e.target.value)}
              opcoes={[
                { valor: '24', rotulo: '1 dia' },
                { valor: '72', rotulo: '3 dias' },
                { valor: '168', rotulo: '7 dias' },
                { valor: '720', rotulo: '30 dias' },
              ]}
            />
            <Campo
              rotulo="Máximo de usos"
              type="number"
              min={1}
              max={100}
              ajuda="Quantas pessoas podem usar o mesmo link."
              value={maximo}
              onChange={(e) => setMaximo(e.target.value)}
            />
          </div>

          {tipo === 'representantes' && (
            <Aviso tom="informacao" titulo="O formulário pede estes papéis">
              <ul className="list-disc pl-5">
                {PAPEIS_DE_REPRESENTANTES.map((papel) => (
                  <li key={papel}>{papel}</li>
                ))}
              </ul>
            </Aviso>
          )}

          <Aviso tom="informacao">
            O link é uma capacidade: quem tiver o endereço consegue se cadastrar nesta empresa até o
            prazo ou o limite acabarem. Envie só a quem deve.
          </Aviso>

          {gerar.isError && <ErroDaApi titulo="Não foi possível gerar" erro={gerar.error} />}
        </div>
      )}
    </Modal>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

function semPermissao(erro: unknown): boolean {
  return erro instanceof ErroApi && erro.status === 403;
}

function situacao(l: LinkResumo): string {
  if (!l.isActive) return 'Revogado';
  if (l.expirado) return 'Expirado';
  if (l.esgotado) return 'Esgotado';
  return 'Válido';
}

function tomDoLink(l: LinkResumo): TomSemantico {
  if (!l.isActive || l.expirado) return 'neutro';
  if (l.esgotado) return 'atencao';
  return 'positivo';
}
